/* Rocket 88 — Cookie Consent + Region Selector
 *
 * NAMING: the banner and overlay are #r88-consent-banner / #r88-consent-overlay
 * and their hidden state is the class .r88-is-hidden. These were previously
 * #cookie-banner / #cookie-overlay / .hidden, which are generic names that
 * content-blocker filter lists and theme CSS can match. Storage keys
 * (r88_cookie_consent etc.) are deliberately UNCHANGED so existing visitors
 * keep their saved choices. HTML, JS and SCSS must be deployed together.
 *
 * REGION: a region is only treated as known once the visitor has chosen it
 * (or arrived from the microsite). Being on eu. / us. does not skip the
 * region step, so visitors on the wrong storefront can switch.
 * Shopify Customer Privacy API integration
 *
 * FLOW (unchanged in purpose)
 *   Step 1: cookie consent  →  Step 2: region choice  →  banner + overlay dismissed
 *
 * REGION PRE-PASSING (from alanparsonsprojectbook.com microsite)
 *   The microsite sets the r88_region cookie on .rocket88books.com before
 *   navigating here. If region is already known, the region selector step
 *   is skipped entirely.
 *
 * EARLIER VERSION (overlay "stuck on" fixes)
 *   - Overlay visibility controlled in ONE place (setBannerVisible).
 *   - Shopify setTrackingConsent / loadFeatures guarded with timeouts.
 *   - Region step skipped when a region is already saved.
 *   - Region step is escapable (Escape / overlay click defers for the session).
 *   - pageshow / storage handlers re-sync state; stored values validated.
 *
 * CHANGES IN THIS VERSION (visitors must never be stuck behind the overlay)
 *   1. Missing markup: if any required element is absent, the script hides
 *      the banner/overlay, switches non-essential cookies off, and stops.
 *   2. Fail open: if the banner's controls are hidden or covered (ad-blocker
 *      cosmetic filters, broken CSS, tiny viewports with no scroll), the
 *      banner and overlay are removed. Checked ~1.5s after the banner shows,
 *      re-checked once, and re-checked on resize/rotation. A banner that is
 *      merely taller than the screen but scrollable is NOT treated as broken.
 *   3. Step 1 is now escapable too (Escape / overlay click).
 *
 *   IMPORTANT — what "leaving without a choice" means (items 1–3):
 *     - Non-essential cookies are switched OFF (same effect as "Reject").
 *     - NO consent is recorded, so the visitor can be asked again later.
 *     - For the rest of the browser session the banner is not shown again
 *       automatically (sessionStorage flag), so it doesn't nag on every page.
 *       "Cookie settings" in the footer still opens it on request.
 *     - If no consent is stored, nothing is pushed to Shopify's API.
 *     - A visitor who ALREADY gave consent is never switched off by this path.
 */
(function () {
  'use strict';

  var CONSENT_KEY        = 'r88_cookie_consent';
  var REGION_KEY         = 'r88_region';
  var CCPA_KEY           = 'r88_ccpa_optout';
  var DEFER_KEY          = 'r88_region_deferred';    // sessionStorage only
  var CONSENT_DEFER_KEY  = 'r88_consent_deferred';   // sessionStorage only
  var EXPIRY_DAYS        = 365;
  var CONSENT_VERSION    = '2';
  var SHOPIFY_TIMEOUT    = 2500;   // ms to wait for setTrackingConsent callback
  var FEATURES_TIMEOUT   = 3000;   // ms to wait for Shopify.loadFeatures callback
  var USABILITY_DELAY    = 1500;   // ms after showing before checking controls
  var USABILITY_RETRY    = 1000;   // ms before the confirming second check
  var HIDDEN_CLASS       = 'r88-is-hidden';   // state class on banner + overlay

  var shopifyReady = false;
  var consentBusy  = false;
  var currentStep  = 1;
  var memFlags     = {};           // fallback if sessionStorage is unavailable

  var banner     = document.getElementById('r88-consent-banner');
  var overlay    = document.getElementById('r88-consent-overlay');
  var panel      = document.getElementById('preferences-panel');
  var btnSave    = document.getElementById('btn-save');
  var btnManage  = document.getElementById('btn-manage');
  var apiStatus  = document.getElementById('api-status');
  var track      = document.getElementById('steps-track');
  var stepCookie = document.getElementById('step-cookie');
  var stepRegion = document.getElementById('step-region');

  function denyAll() {
    return { analytics: false, marketing: false, preferences: false };
  }

  /* ── 1. Bail out cleanly if the markup is incomplete ─────── *
   *  (e.g. a theme/storefront that ships different markup)     */
  var required = [
    banner, overlay, panel, btnSave, btnManage, track, stepCookie, stepRegion,
    document.getElementById('btn-accept-all'),
    document.getElementById('btn-reject-all'),
    document.getElementById('region-uk')
  ];
  var markupComplete = required.every(function (el) { return !!el; });

  if (!markupComplete) {
    console.warn('[Rocket88] Consent markup incomplete — skipping banner');
    [banner, overlay].forEach(function (el) { if (el) el.style.display = 'none'; });
    // Non-essential cookies stay off; nothing is recorded.
    // (applyConsent is a hoisted function declaration, safe to call here.)
    try { applyConsent(denyAll()); } catch (_) {}
    // Stubs so footer/header links don't throw ReferenceErrors.
    window.r88OpenCookieSettings = function () {};
    window.r88OpenRegion = function () {};
    return;
  }

  /* ── Small helpers ───────────────────────────────────────── */
  function focusById(id, delay) {
    setTimeout(function () {
      var el = document.getElementById(id);
      if (el && typeof el.focus === 'function') el.focus();
    }, delay);
  }

  function setClass(el, cls, on) {
    if (on) el.classList.add(cls);
    else    el.classList.remove(cls);
  }

  /* ── Cookie storage ──────────────────────────────────────── */
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var domain = window.location.hostname.indexOf('rocket88books.com') !== -1
      ? ';domain=.rocket88books.com'
      : '';
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() + ';path=/' + domain + ';SameSite=Lax;Secure';
  }

  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g,'\\$&') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  /* Read a value from cookie, falling back to localStorage. If only the
   * localStorage copy survives, restore the cookie so the two agree. */
  function readStored(key) {
    var fromCookie = null;
    try { fromCookie = getCookie(key); } catch (_) {}
    if (fromCookie) return fromCookie;
    var fromLs = null;
    try { fromLs = localStorage.getItem(key); } catch (_) {}
    if (fromLs) {
      try { setCookie(key, fromLs, EXPIRY_DAYS); } catch (_) {}
    }
    return fromLs;
  }

  function writeStored(key, value) {
    try { setCookie(key, value, EXPIRY_DAYS); } catch (_) {}
    try { localStorage.setItem(key, value); } catch (_) {}
  }

  /* ── Session-only flags (with in-memory fallback) ────────── */
  function isFlagSet(key) {
    if (memFlags[key]) return true;
    try { return sessionStorage.getItem(key) === '1'; } catch (_) { return false; }
  }

  function setFlag(key, on) {
    memFlags[key] = !!on;
    try {
      if (on) sessionStorage.setItem(key, '1');
      else    sessionStorage.removeItem(key);
    } catch (_) {}
  }

  function isRegionDeferred()  { return isFlagSet(DEFER_KEY); }
  function setRegionDeferred(on)  { setFlag(DEFER_KEY, on); }
  function isConsentDeferred() { return isFlagSet(CONSENT_DEFER_KEY); }
  function setConsentDeferred(on) { setFlag(CONSENT_DEFER_KEY, on); }

  /* ── Toast ───────────────────────────────────────────────── */
  function showToast(msg) {
    var t = document.getElementById('toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('show');
    setTimeout(function(){ t.classList.remove('show'); }, 3200);
  }

  /* ── Banner + overlay visibility (single source of truth) ── */
  function isBannerHidden() {
    return banner.classList.contains(HIDDEN_CLASS);
  }

  var usabilityTimer = null;

  function setBannerVisible(visible) {
    setClass(banner,  HIDDEN_CLASS, !visible);
    setClass(overlay, HIDDEN_CLASS, !visible);
    // Clear any inline display:none left by a forced dismissal.
    if (visible) {
      banner.style.display  = '';
      overlay.style.display = '';
      scheduleUsabilityCheck(USABILITY_DELAY, false);
    } else {
      clearTimeout(usabilityTimer);
    }
  }

  /* force = also set inline display:none, in case the .r88-is-hidden class has
   * no effect (broken or missing CSS). */
  function dismissBanner(force) {
    setBannerVisible(false);
    if (force) {
      banner.style.display  = 'none';
      overlay.style.display = 'none';
    }
  }

  function showBanner() {
    goToStep(1, false);
    setBannerVisible(true);
    focusById('btn-accept-all', 120);
  }

  /* ── 2. Usability check (fail open) ──────────────────────── *
   *  Fails only if the primary control is genuinely unreachable: *
   *  hidden, zero-size, covered by something else, or off-screen *
   *  with no way to scroll to it. "Below the fold but the        *
   *  banner scrolls" is fine.                                    */
  function controlsUsable() {
    var cs = window.getComputedStyle(banner);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;

    var br = banner.getBoundingClientRect();
    if (!br.width || !br.height) return false;

    var btn = document.getElementById(currentStep === 2 ? 'region-uk' : 'btn-accept-all');
    if (!btn) return false;

    var r = btn.getBoundingClientRect();
    if (!r.width || !r.height) return false;            // display:none / collapsed

    var vw = window.innerWidth  || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;

    // Visible part of the button within the viewport
    var left   = Math.max(r.left, 0),  right  = Math.min(r.right, vw);
    var top    = Math.max(r.top, 0),   bottom = Math.min(r.bottom, vh);
    var inView = right > left && bottom > top;

    if (inView) {
      // Is something else sitting on top of it? Sample a few points so one
      // small floating widget doesn't cause a false alarm.
      var cy = (top + bottom) / 2;
      var xs = [(left + right) / 2, left + (right - left) * 0.25, left + (right - left) * 0.75];
      for (var i = 0; i < xs.length; i++) {
        var hit = document.elementFromPoint(xs[i], cy);
        if (hit && (hit === btn || btn.contains(hit))) return true;
      }
      return false;
    }

    // Button is off-screen. That's only OK if the user can scroll to it.
    if (cs.position !== 'fixed') return true;           // page scroll can reach it
    var oy = cs.overflowY;
    var scrollable = (oy === 'auto' || oy === 'scroll') &&
                     banner.scrollHeight > banner.clientHeight + 1;
    return scrollable;
  }

  function scheduleUsabilityCheck(delay, retried) {
    clearTimeout(usabilityTimer);
    usabilityTimer = setTimeout(function () {
      if (isBannerHidden()) return;
      var ok = true;
      try { ok = controlsUsable(); }
      catch (e) { console.warn('[Rocket88] usability check error:', e); ok = true; }
      if (ok) return;
      if (!retried) { scheduleUsabilityCheck(USABILITY_RETRY, true); return; }
      console.warn('[Rocket88] Consent controls not usable — failing open');
      leaveWithoutChoice(true);
    }, delay);
  }

  /* ── Leave without a choice (fail-open, Escape, overlay click) ─ *
   *  Non-essential cookies OFF, nothing recorded, banner not shown *
   *  again this session. Never overrides consent already given.    */
  function leaveWithoutChoice(force) {
    if (!loadConsent()) {
      try { applyConsent(denyAll()); } catch (e) { console.warn('[Rocket88] deny failed:', e); }
      setConsentDeferred(true);
    }
    var region = loadRegion();
    if (!region) setRegionDeferred(true);
    dismissBanner(force);
    if (region) updateRegionLink(region);
    else        showDeferredRegionLink();
  }

  /* ── Step navigation ─────────────────────────────────────── */
  function goToStep(n, animate) {
    currentStep = (n === 2) ? 2 : 1;
    if (animate === false) track.style.transition = 'none';
    else track.style.transition = '';
    if (n === 2) {
      track.style.transform = 'translateX(-50%)';
      stepCookie.setAttribute('aria-hidden','true');
      stepRegion.setAttribute('aria-hidden','false');
      focusById('region-uk', 480);
    } else {
      track.style.transform = 'translateX(0)';
      stepCookie.setAttribute('aria-hidden','false');
      stepRegion.setAttribute('aria-hidden','true');
    }
    if (animate === false) {
      requestAnimationFrame(function(){
        requestAnimationFrame(function(){ track.style.transition = ''; });
      });
    }
    if (!isBannerHidden()) scheduleUsabilityCheck(USABILITY_DELAY, false);
  }

  /* ── Consent storage ─────────────────────────────────────── */
  function saveConsent(choices) {
    var payload = JSON.stringify({
      necessary:   true,
      analytics:   !!choices.analytics,
      marketing:   !!choices.marketing,
      preferences: !!choices.preferences,
      timestamp:   new Date().toISOString(),
      version:     CONSENT_VERSION
    });
    writeStored(CONSENT_KEY, payload);
  }

  function loadConsent() {
    try {
      var raw = readStored(CONSENT_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      // Ignore malformed or old-format values so they can't leave us in a
      // half-state; the user will simply be asked again.
      if (!c || typeof c !== 'object' || c.version !== CONSENT_VERSION ||
          typeof c.analytics !== 'boolean' ||
          typeof c.marketing !== 'boolean' ||
          typeof c.preferences !== 'boolean') {
        return null;
      }
      return c;
    } catch (_) { return null; }
  }

  /* ── Region storage ──────────────────────────────────────── */
  var VALID_REGIONS = { uk:1, us:1, eu:1, row:1 };

  function saveRegion(region) {
    writeStored(REGION_KEY, region);
  }

  function loadRegion() {
    try {
      var r = readStored(REGION_KEY);
      return (r && VALID_REGIONS[r]) ? r : null;
    } catch (_) { return null; }
  }

  /* ── CCPA storage ────────────────────────────────────────── */
  function saveCcpaOptout(optedOut) {
    var payload = JSON.stringify({
      optedOut:  optedOut,
      timestamp: new Date().toISOString()
    });
    writeStored(CCPA_KEY, payload);
  }

  function loadCcpaOptout() {
    try {
      var raw = readStored(CCPA_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch(_){ return null; }
  }

  /* ── Shopify Customer Privacy API ────────────────────────── *
   *  onDone is called exactly once: true on success, false on  *
   *  error, unavailability, exception or timeout.              */
  function pushToShopify(choices, onDone) {
    var finished = false;
    var timer = null;

    function done(ok) {
      if (finished) return;
      finished = true;
      if (timer) clearTimeout(timer);
      if (onDone) onDone(ok);
    }

    timer = setTimeout(function(){ done(false); }, SHOPIFY_TIMEOUT);

    if (!shopifyReady || !window.Shopify || !window.Shopify.customerPrivacy) {
      done(false);
      return;
    }

    try {
      window.Shopify.customerPrivacy.setTrackingConsent(
        {
          analytics:    !!choices.analytics,
          marketing:    !!choices.marketing,
          preferences:  !!choices.preferences,
          sale_of_data: !!choices.sale_of_data
        },
        function(result) {
          done(!(result && result.error));
        }
      );
    } catch (e) {
      console.warn('[Rocket88] setTrackingConsent threw:', e);
      done(false);
    }
  }

  /* ── Delete cookies by name/prefix ──────────────────────── */
  function deleteCookies(names, prefixes) {
    var domains = [
      window.location.hostname,
      '.' + window.location.hostname,
      '.rocket88books.com'
    ];
    names.forEach(function(name) {
      domains.forEach(function(domain) {
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + domain;
      });
    });
    if (prefixes && prefixes.length) {
      var all = document.cookie.split(';');
      all.forEach(function(c) {
        var cookieName = c.trim().split('=')[0];
        prefixes.forEach(function(prefix) {
          if (cookieName.startsWith(prefix)) {
            domains.forEach(function(domain) {
              document.cookie = cookieName + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + domain;
            });
          }
        });
      });
    }
  }

  function applyConsent(choices) {
    window.dispatchEvent(new CustomEvent('r88:consent', { detail: choices }));

    if (choices.analytics) {
      console.info('[Rocket88] Analytics: ENABLED');
    } else {
      deleteCookies(
        ['_ga', '_gid', '_gat', '_gat_UA', 'PREF', 'NID'],
        ['_ga_', '_gat_', '_utm']
      );
      console.info('[Rocket88] Analytics: BLOCKED — cookies deleted');
    }

    if (choices.marketing) {
      console.info('[Rocket88] Marketing: ENABLED');
    } else {
      deleteCookies(
        ['_fbp', '_fbc', '__utma', '__utmb', '__utmc', '__utmt', '__utmz'],
        ['_mc_', '__hstc', 'hubspot']
      );
      console.info('[Rocket88] Marketing: BLOCKED — cookies deleted');
    }

    if (choices.preferences) {
      console.info('[Rocket88] Preferences: ENABLED');
    } else {
      console.info('[Rocket88] Preferences: BLOCKED');
    }
  }

  /* ── Give consent and advance ────────────────────────────── *
   *  If a region is already saved (microsite pre-pass, earlier *
   *  visit, or re-opening Cookie settings) dismiss straight    *
   *  after consent; otherwise move to the region step.         */
  function giveConsent(choices) {
    if (consentBusy) return;           // ignore double-clicks
    consentBusy = true;

    pushToShopify(choices, function(ok) {
      consentBusy = false;
      try {
        saveConsent(choices);
        applyConsent(choices);
        setConsentDeferred(false);
      } catch (e) {
        console.error('[Rocket88] Error applying consent:', e);
      }

      if (loadRegion()) {
        dismissBanner();
      } else {
        goToStep(2, true);
      }

      if (!ok) console.info('[Rocket88] Shopify unavailable or slow — consent saved locally');
    });
  }

  /* ── Region link badge ───────────────────────────────────── */
  var REGION_LABELS  = { uk:'UK', us:'US', eu:'EU', row:'Intl' };
  var REGION_CLASSES = { uk:'row', us:'us', eu:'eu', row:'row' };

  function updateRegionLink(region) {
    var link  = document.getElementById('geo-button');
    var badge = document.getElementById('current-region-badge');
    if (!link || !badge) return;
    if (region) {
      badge.textContent  = REGION_LABELS[region] || region.toUpperCase();
      badge.className    = REGION_CLASSES[region] || '';
      link.style.display = 'inline-flex';
    } else {
      badge.textContent  = '';
      badge.className    = '';
      link.style.display = 'none';
    }
  }

  /* Shown when the user postponed the region choice, so they can find it again */
  function showDeferredRegionLink() {
    var link  = document.getElementById('geo-button');
    var badge = document.getElementById('current-region-badge');
    if (!link || !badge) return;
    badge.textContent  = 'Region';
    badge.className    = 'row';
    link.style.display = 'inline-flex';
  }

  function deferRegionChoice() {
    setRegionDeferred(true);
    dismissBanner();
    showDeferredRegionLink();
    showToast('You can choose your region any time from the header.');
  }

  /* ── 3. Escape / overlay click works on both steps ───────── */
  function dismissWithoutChoice() {
    if (isBannerHidden()) return;
    if (currentStep === 2) {
      deferRegionChoice();
    } else {
      leaveWithoutChoice(false);
      showToast('No cookie choice saved. Non-essential cookies are off. Change this any time under Cookie settings.');
    }
  }

  /* ── CCPA opt-out button ─────────────────────────────────── */
  function initCcpaButton() {
    var btn = document.getElementById('btn-ccpa-optout');
    if (!btn) return;
    var isUs = window.location.hostname.startsWith('us.');
    btn.style.display = isUs ? 'block' : 'none';
    if (!isUs) return;
    var savedCcpa = loadCcpaOptout();
    if (savedCcpa && savedCcpa.optedOut) {
      var txt = document.createElement('p');
      txt.className = 'ccpa-optout-confirmed';
      txt.textContent = 'Data sale opt-out: on';
      btn.parentNode.replaceChild(txt, btn);
      return;
    }
    btn.addEventListener('click', function() {
      saveCcpaOptout(true);
      saveConsent({ analytics: false, marketing: false, preferences: false });
      pushToShopify({ analytics: false, marketing: false, preferences: false, sale_of_data: true }, function(){});
      applyConsent({ analytics: false, marketing: false, preferences: false });
      var txt = document.createElement('p');
      txt.className = 'ccpa-optout-confirmed';
      txt.textContent = 'Data sale opt-out: on';
      btn.parentNode.replaceChild(txt, btn);
      showToast('Opt-out saved.');
    });
  }

  /* ── Populate toggles from saved consent ────────────────── */
  function populateToggles(choices) {
    if (!choices) return;
    var ids = ['analytics', 'marketing', 'preferences'];
    ids.forEach(function(id) {
      var el = document.getElementById('pref-' + id);
      if (el) el.checked = !!choices[id];
    });
  }

  /* ── Open cookie settings ───────────────────────────────── */
  function openCookieSettings() {
    setConsentDeferred(false);          // explicit request overrides "not now"
    populateToggles(loadConsent());
    goToStep(1, false);
    setBannerVisible(true);
    focusById('btn-accept-all', 120);
  }
  window.r88OpenCookieSettings = openCookieSettings;

  /* ── Open region selector ────────────────────────────────── */
  function openRegionSelector() {
    setRegionDeferred(false);
    goToStep(2, !isBannerHidden());     // no slide animation if banner is hidden
    setBannerVisible(true);
  }
  window.r88OpenRegion = openRegionSelector;

  /* ── Region selection ────────────────────────────────────── */
  function redirectToHost(host, loc) {
    var targetURL = loc.protocol + '//' + host + loc.pathname + loc.search + loc.hash;
    showToast('Redirecting\u2026');
    setTimeout(function(){ window.location.href = targetURL; }, 400);
  }

  function handleRegion(region) {
    saveRegion(region);
    setRegionDeferred(false);
    window.dispatchEvent(new CustomEvent('r88:region', { detail:{ region: region } }));
    updateRegionLink(region);

    var hostname = window.location.hostname;
    var onUs   = hostname.startsWith('us.');
    var onEu   = hostname.startsWith('eu.');
    var onMain = !onUs && !onEu;
    var loc    = window.location;

    if (region === 'us') {
      if (onUs) dismissBanner();
      else      redirectToHost('us.rocket88books.com', loc);
    } else if (region === 'eu') {
      if (onEu) dismissBanner();
      else      redirectToHost('eu.rocket88books.com', loc);
    } else {
      if (onMain) dismissBanner();
      else        redirectToHost('rocket88books.com', loc);
    }
  }

  /* ── Re-sync after Back (bfcache) or another tab finishing ─ *
   *  If both consent and region are now saved, the banner and  *
   *  overlay must not be showing.                              */
  function reconcile() {
    try {
      if (!isBannerHidden() && loadConsent() && loadRegion()) {
        dismissBanner();
        updateRegionLink(loadRegion());
      }
    } catch (e) {
      console.warn('[Rocket88] reconcile failed:', e);
    }
  }

  window.addEventListener('pageshow', function(e) {
    if (e.persisted) reconcile();
  });

  window.addEventListener('storage', function(e) {
    if (!e.key || e.key === CONSENT_KEY || e.key === REGION_KEY) reconcile();
  });

  /* Rotation / resize can make the controls unreachable on small screens */
  window.addEventListener('resize', function() {
    if (!isBannerHidden()) scheduleUsabilityCheck(800, false);
  });

  /* ── Event listeners ─────────────────────────────────────── */
  var geoBtn = document.getElementById('geo-button');
  if (geoBtn) {
    geoBtn.addEventListener('click', function(e){
      e.preventDefault();
      openRegionSelector();
    });
  }

  document.getElementById('btn-accept-all').addEventListener('click', function(){
    giveConsent({ analytics:true, marketing:true, preferences:true });
  });

  document.getElementById('btn-reject-all').addEventListener('click', function(){
    giveConsent({ analytics:false, marketing:false, preferences:false });
  });

  btnManage.addEventListener('click', function() {
    if (isBannerHidden()) {
      openCookieSettings();
      setTimeout(function() {
        panel.classList.add('open');
        panel.setAttribute('aria-hidden', 'false');
        btnManage.textContent = 'Hide preferences';
        btnSave.style.display = 'block';
        populateToggles(loadConsent());
      }, 150);
      return;
    }
    var isOpen = panel.classList.toggle('open');
    panel.setAttribute('aria-hidden', String(!isOpen));
    btnManage.textContent = isOpen ? 'Hide preferences' : 'Manage preferences';
    btnSave.style.display = isOpen ? 'block' : 'none';
    if (isOpen) populateToggles(loadConsent());
  });

  btnSave.addEventListener('click', function() {
    giveConsent({
      analytics:   document.getElementById('pref-analytics').checked,
      marketing:   document.getElementById('pref-marketing').checked,
      preferences: document.getElementById('pref-preferences').checked
    });
  });

  ['uk','us','eu','row'].forEach(function(r) {
    var btn = document.getElementById('region-' + r);
    if (btn) btn.addEventListener('click', function(){ handleRegion(r); });
  });

  /* Clicking the overlay leaves without a choice (both steps) */
  overlay.addEventListener('click', dismissWithoutChoice);

  /* ── Keyboard: Escape (both steps) + focus trap ──────────── */
  document.addEventListener('keydown', function(e) {
    if (isBannerHidden()) return;

    if (e.key === 'Escape') {
      dismissWithoutChoice();
      return;
    }

    if (e.key !== 'Tab') return;
    var els   = banner.querySelectorAll('button:not([disabled]),input:not([disabled]),a[href]');
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey) { if (document.activeElement === first){ e.preventDefault(); last.focus(); } }
    else            { if (document.activeElement === last)  { e.preventDefault(); first.focus(); } }
  });

  /* ── Init ────────────────────────────────────────────────── *
   *  Region inference (before decision tree):                  *
   *    referrer = microsite (main domain only) → 'uk', saved   *
   *    eu. / us. subdomain   → NOT inferred: the region step   *
   *      is still shown so the visitor can confirm or change   *
   *    otherwise             → not inferred, modal shown       *
   *                                                            *
   *  Decision tree:                                            *
   *  1. Both consent + region saved → silent apply, no banner  *
   *  1b. No consent, but the visitor left without choosing     *
   *     earlier this session → cookies off, no banner (region  *
   *     step still offered unless deferred)                    *
   *  2. Region saved/inferred, no consent yet →                *
   *     show cookie step only, skip region step after consent  *
   *  3. Region saved as 'us', no consent → opt-out             *
   *     model: assume consent, hide banner                     *
   *     NOTE: unreachable, as case 2 already matches this      *
   *     situation, so US visitors are shown the banner. Left   *
   *     as-is deliberately: reordering would make US visitors  *
   *     auto-consent, which is a policy decision, not a bug    *
   *     fix. Decide, then move this block above case 2.        *
   *  4. Consent saved, no region → skip to region step         *
   *     (unless the user deferred it this session)             *
   *  5. Nothing saved → full banner flow                       */
  function init() {
    var savedConsent = loadConsent();
    var savedRegion  = loadRegion();

    console.log('[R88] savedConsent:', savedConsent);
    console.log('[R88] savedRegion:', savedRegion);
    console.log('[R88] hostname:', window.location.hostname);

    // For visitors arriving from the microsite on the main domain, infer 'uk'
    // from the referrer — they selected GBP so landed here intentionally.
    // The eu./us. subdomain says which storefront the visitor is ON, not that
    // they have confirmed it is the right one for them (they may have followed
    // a link to the wrong storefront). So it is deliberately NOT saved as their
    // region: the region step is still shown after consent until they pick one.
    // Only a region they chose, or one passed from the microsite, counts.
    var onRegionHost = window.location.hostname.startsWith('eu.') ||
                       window.location.hostname.startsWith('us.');
    if (!savedRegion && !onRegionHost) {
      var fromMicrosite = (document.referrer || '').indexOf('alanparsonsprojectbook.com') !== -1;
      if (fromMicrosite) { savedRegion = 'uk'; saveRegion('uk'); }
    }

    // 1. Both already saved — apply silently, hide banner, show region link
    if (savedConsent && savedRegion) {
      dismissBanner();
      applyConsent(savedConsent);
      updateRegionLink(savedRegion);
      return;
    }

    // 1b. Visitor already left without choosing this session — keep
    //     non-essential cookies off and don't nag on every page.
    if (!savedConsent && isConsentDeferred()) {
      applyConsent(denyAll());
      if (savedRegion) {
        updateRegionLink(savedRegion);
        dismissBanner();
      } else {
        showRegionStepOrDefer();
      }
      return;
    }

    // 2. Region known but no consent yet — cookie step only.
    //    (giveConsent dismisses after consent because a region is saved.)
    if (!savedConsent && savedRegion) {
      updateRegionLink(savedRegion);
      window._r88SkipRegionStep = true;   // kept for backward compatibility
      showBanner();
      return;
    }

    // 3. US opt-out model — see note above; currently unreachable.
    if (!savedConsent && savedRegion === 'us') {
      var usChoices = { analytics: true, marketing: true, preferences: true };
      saveConsent(usChoices);
      applyConsent(usChoices);
      updateRegionLink('us');
      dismissBanner();
      return;
    }

    // 4. Consent saved but no region — show region step (unless deferred)
    if (savedConsent && !savedRegion) {
      applyConsent(savedConsent);
      showRegionStepOrDefer();
      return;
    }

    // 5. Nothing saved — check Shopify API then show full banner.
    //    A timeout guarantees the banner appears even if Shopify's
    //    loader never calls back (e.g. blocked by an extension).
    if (window.Shopify && window.Shopify.loadFeatures) {
      var resolved = false;
      var fallbackTimer = setTimeout(function() {
        if (resolved) return;
        resolved = true;
        console.info('[Rocket88] Shopify loadFeatures timed out — showing banner');
        showBanner();
      }, FEATURES_TIMEOUT);

      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function(error) {
            clearTimeout(fallbackTimer);
            if (!error) shopifyReady = true;   // usable even if we already timed out
            if (resolved) return;
            resolved = true;

            if (error) { showBanner(); return; }

            var existing = null;
            try { existing = window.Shopify.customerPrivacy.currentVisitorConsent(); }
            catch (e) { existing = null; }

            if (existing && existing.analytics !== '') {
              var fromShopify = {
                analytics:   existing.analytics   === 'yes',
                marketing:   existing.marketing   === 'yes',
                preferences: existing.preferences === 'yes'
              };
              // Persist locally too, so the cookie step doesn't come back
              // after the region is chosen.
              saveConsent(fromShopify);
              applyConsent(fromShopify);
              showRegionStepOrDefer();
            } else {
              showBanner();
            }
          }
        );
      } catch (e) {
        clearTimeout(fallbackTimer);
        if (!resolved) { resolved = true; showBanner(); }
      }
    } else {
      showBanner();
    }
  }

  function showRegionStepOrDefer() {
    if (isRegionDeferred()) {
      dismissBanner();
      showDeferredRegionLink();
      return;
    }
    goToStep(2, false);
    setBannerVisible(true);
    focusById('region-uk', 100);
  }

  function safeInit() {
    try {
      init();
    } catch (e) {
      console.error('[Rocket88] init failed:', e);
      // Never leave the user with a half-state: if we can't tell what's
      // saved, ask again rather than leave the page in limbo. The usability
      // check will still fail open if the banner can't be used.
      try {
        if (!(loadConsent() && loadRegion())) showBanner();
        else dismissBanner();
      } catch (_) { dismissBanner(true); }
    }
    initCcpaButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }

})();